/**
 * @file 内容工具
 * @author Yangholmes 2023-12-29
 */
import DOMPurify from 'dompurify';
import { ContentTypeEnum } from '@/api/thread';
import type { IEmoji, IGetEmojiListRes } from '@/api/common';
import type { ICommentListRes } from '@/api/detail';
import type { IEditorContentItem, IRelatedContent } from '@/components/ModuleEditor/config';

const defaultImage = require('@/assets/icon/photo-default-cover.png');

/**
 * 将两层的表情包列表展开成一层
 * @param raw 接口返回的表情包
 * @returns
 */
export function flatEmojies(raw: void | IGetEmojiListRes) {
  const _emojies: IEmoji[] = []
  const { items = [] } = raw || {}
  items.forEach((item) => {
    const shownOnReply = !!item.shown_on_reply
    const smiles = item.smiles.map(e => ({
      ...e,
      shownOnReply,
      type: item.name
    }))
    _emojies.push(...smiles)
  });
  return _emojies
}

/**
 * 将文本中表情包字符串替换为图片标签
 * @param message 文本
 * @param emojies 表情包列表
 * @returns
 */
export function encodeReplyEmojies(message: string, emojies: IEmoji[]) {
  const m = (message || '').replace(/(\{:.*?:\})/g, (_, p) => {
    const url = emojies.find(emo => emo.code === p)?.url
    return url ? `<img class="emoji" style="max-width: 5em; display: inline;" src="${url}" />` : p
  })
  return m;
}

/**
 * 安全插入评论列表，去除重复的评论
 * @param replyList 旧的评论列表
 * @param newReplyList 新获取的评论列表
 * @return 新评论安全插入后的评论列表
 */
export function safePushNewReply<T extends ICommentListRes>(replyList: T[], newReplyList: T[]) {
  const rl = replyList.slice()
  for (let i = 0; i < newReplyList.length; i++) {
    if (replyList.findIndex(item => item.pid === newReplyList[i].pid) === -1) {
      rl.push(newReplyList[i])
    }
  }
  return rl;
}

export const replaceEmojiCodeToImg = (message: string, emojiData: IEmoji[], styles?: any, isQuote?: boolean) => {
  // 把表情替换成图片
  let matchEmoji: IEmoji | undefined;
  return message.replace(/{:\d_\d{3}:}/g, (matchedString) => {
    emojiData.forEach((item) => {
      if (item.code === matchedString) matchEmoji = item;
    });
    // 引用里把表情Code码替换成"[表情]"
    if (isQuote) return ' [表情] '
    // 评论里把表情Code码替换成图片
    return matchEmoji ? `<img class="${styles.commentEmojiImage} comment-emoji" src="${matchEmoji.url}" />` : matchedString;
  });
};

/* 
  处理评论内容:
    1. 提取旧版用户名，移除.quote，拼成"<b>回复 旧版用户名：<b>内容"
    2. 中间版(在最开始评论改版时,新发的回复内容会被处理成"<b>回复 用户名：<b>内容"),当前改版改成展示引用内容,所以把中间版这段时间产生的<b>回复:xxx</b>标签删掉)
    3. 把表情Code码转成.comment-emoji img标签
    4. 处理所有非表情(非.comment-emoji)图片，移除旧版图片自带的onload等属性，添加.reply-content-img，设置onerror
    5. 转回字符串

    注: 此方法没有防止XSS功能,使用dangerouslySetInnerHTML时需再经过一步createMarkup(disposeMessage(message))
*/
export const disposeMessage = (message: string, emojiData: IEmoji[], styles?: any, isQuote?: boolean, isAction?: boolean) => {
  let messageContent = message;
  // 1. 提取并转换旧版引用
  const parser = new DOMParser();
  const quoteParsedHtml = parser.parseFromString(messageContent, 'text/html');
  if (quoteParsedHtml.querySelector('div.quote')) {
    // 获取"被回复人"和"评论内容"
    const replyName = quoteParsedHtml.querySelector('.customName')?.textContent;
    const fontReplyName = quoteParsedHtml.querySelector('a.nopicture')?.querySelector('font')?.textContent;
    quoteParsedHtml.querySelector('div.quote')?.remove();
    const removeQuoteText = quoteParsedHtml.querySelector('body')?.innerHTML;
    if (isQuote) {
      messageContent = removeQuoteText || messageContent;
    } else {
      messageContent = `<b class="old_reply">回复 ${replyName || fontReplyName || ''}：</b>${removeQuoteText}` || messageContent;
    }
  } else {
    // 2. 移除<b>回复:xxx</b>标签
    const bTags = quoteParsedHtml.querySelectorAll('b');
    bTags.forEach(bTag => {
      if (/^回复\s.+/.test(bTag.innerHTML)) {
        bTag.remove();
      }
    });
    const removeBTagText = quoteParsedHtml.querySelector('body')?.innerHTML;
    messageContent = removeBTagText || messageContent;
  }
  // 3. 替换表情
  const emojiReplacedMessage = replaceEmojiCodeToImg(messageContent, emojiData, styles, isQuote);
  // 4. 处理图片
  const parsedHtml = parser.parseFromString(emojiReplacedMessage, 'text/html');
  const imgs = parsedHtml.querySelectorAll('img:not(.comment-emoji)');
  imgs?.forEach(img => {
    img?.removeAttribute('onload');
    img?.removeAttribute('onmouseover');
    img?.removeAttribute('onclick');
    img.classList.add('reply-content-img');
    setImgFallbackOnError(img);
    const image = img as HTMLImageElement;
    if (image.src.startsWith('blob:')) {
      // 某些src为blob:的图片,会使onerror失效
      img.setAttribute('src', defaultImage?.default?.src);
    }
  });
  // 引用里移除img标签
  if (isQuote || isAction) {
    const imgs = parsedHtml.querySelectorAll('img:not(.comment-emoji)');
    imgs?.forEach(img => {
      if (isAction) {
        // 更多弹窗内显示"当前评论"的特殊处理
        const textNode = document.createTextNode('[图片]');
        img?.parentNode?.replaceChild(textNode, img);
      }
      img?.remove();
    });
  }
  // 将处理后的 HTML 对象再转回 HTML 字符串
  return parsedHtml.querySelector('body')?.innerHTML || emojiReplacedMessage;
}

/* 返回评论中所有图片的URL地址数组 */
export const extractImgURls = (message: string | undefined) => {
  if (message) {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(message, 'text/html');
    // 移除所有 .quote 元素及其子元素
    const quotes = parsedHtml.querySelectorAll('.quote');
    quotes?.forEach(quote => quote?.remove());
    // 取出.quote之外的所有 img 标签的 src
    let imgUrls = [] as string[];
    const imgs = parsedHtml.querySelectorAll('img');
    imgs?.forEach(img => {
      imgUrls.push(img?.src);
    });
    return imgUrls;
  }
  return [];
}

/* 解码HTML */
export const decodeHtmlEntities = (encodedString: string) => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = encodedString;
  return tempElement.innerText;
}

const bypassedHost = 'www.djivideos.com';

/** 为非表情图片挂上由代码控制的兜底 onerror，避免裂图。供 createMarkup 和 disposeMessage 复用。 */
const setImgFallbackOnError = (img: Element) => {
  img.setAttribute('onerror', `this.src = '${defaultImage?.default?.src}'`);
};

// 净化HTML,防止XSS攻击
export const createMarkup = (htmlContent: string, checkIframeHost: boolean = true) =>  {
  const cleanHtml = DOMPurify.sanitize(htmlContent, {
    RETURN_DOM: true,
    FORBID_TAGS: ['script'],  // 禁止脚本标签
    ADD_TAGS: ['iframe', 'img'],  // 允许 iframe 标签
    // 不白名单 onerror：DOMPurify 默认移除所有事件处理器属性，避免用户提交的 onerror 造成存储型 XSS。
    ADD_ATTR: ['allowfullscreen', 'frameborder']
  });
  const bypassedIframes = cleanHtml.querySelectorAll('iframe');
  bypassedIframes.forEach(iframe => {
    // 只允许指定域名的 iframe(评论的视频)
    const url = new URL(iframe.src);
    if (checkIframeHost && url.hostname !== bypassedHost) {
      iframe.remove();
    }
  });
  // DOMPurify 已剥离全部 onerror，这里仅为非表情图片重新挂上由我们控制的兜底 onerror，避免裂图。
  const safeImgs = cleanHtml.querySelectorAll('img:not(.comment-emoji)');
  safeImgs.forEach(setImgFallbackOnError);
  const bypassedCleanHtml = cleanHtml.innerHTML;
  return { __html: bypassedCleanHtml};
}

export const checkArticalContentByTrim = (content?: IRelatedContent & { message: IEditorContentItem[] }) => {
  if (!(content?.title || '').trim()) {
    return 'error_thread_title_minimum';
  }
  if(!content?.message || content?.message?.length <= 0) {
    return 'error_thread_content_minimum';
  }
  const hasOtherType = content.message.find((item) => item?.type !== ContentTypeEnum.text);
  if(hasOtherType) {
    return undefined;
  } else {
    const text = content.message.reduce((prev, cur: IEditorContentItem) => {
      return `${prev}${cur?.desc || ''}`;
    }, '')
    return !text.trim() ? 'error_thread_content_minimum' : undefined;
  }
}
